/**
 * Guest details form: name, email, phone, notes, GDPR consent.
 * Validates inline and reports overall validity to the parent.
 */

import { cleanText, escapeHTML, isValidEmail, isValidPhone } from '../utils/formatting.js';

export interface GuestFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
  gdprConsent: boolean;
}

export interface GuestFormOptions {
  initial?: Partial<GuestFormState>;
  onValidityChange: (valid: boolean) => void;
  onStateChange: (state: GuestFormState) => void;
  privacyUrl?: string;
}

interface FieldDef {
  key: keyof GuestFormState;
  label: string;
  type: 'text' | 'email' | 'tel';
  autocomplete: string;
  required: boolean;
  half: boolean;
  maxLength: number;
}

const MAX_LENGTHS = {
  firstName: 80,
  lastName: 80,
  email: 254,
  phone: 30,
  notes: 2000,
} as const;

const FIELDS: FieldDef[] = [
  { key: 'firstName', label: 'First name', type: 'text', autocomplete: 'given-name', required: true, half: true, maxLength: MAX_LENGTHS.firstName },
  { key: 'lastName', label: 'Last name', type: 'text', autocomplete: 'family-name', required: true, half: true, maxLength: MAX_LENGTHS.lastName },
  { key: 'email', label: 'Email', type: 'email', autocomplete: 'email', required: true, half: false, maxLength: MAX_LENGTHS.email },
  { key: 'phone', label: 'Mobile phone (so Sara can reach you during your stay)', type: 'tel', autocomplete: 'tel', required: true, half: false, maxLength: MAX_LENGTHS.phone },
];

export class GuestFormStep {
  private readonly host: HTMLElement;
  private readonly opts: GuestFormOptions;
  private state: GuestFormState;
  private touched: Record<keyof GuestFormState, boolean> = {
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    notes: false,
    gdprConsent: false,
  };

  constructor(host: HTMLElement, opts: GuestFormOptions) {
    this.host = host;
    this.opts = opts;
    this.state = {
      firstName: opts.initial?.firstName ?? '',
      lastName: opts.initial?.lastName ?? '',
      email: opts.initial?.email ?? '',
      phone: opts.initial?.phone ?? '',
      notes: opts.initial?.notes ?? '',
      gdprConsent: opts.initial?.gdprConsent ?? false,
    };
    this.render();
    this.opts.onValidityChange(this.isValid());
  }

  destroy(): void {
    this.host.innerHTML = '';
  }

  /** Run after a user attempts to advance to surface every error. */
  showAllErrors(): void {
    (Object.keys(this.touched) as Array<keyof GuestFormState>).forEach((k) => {
      this.touched[k] = true;
    });
    this.refreshErrors();
  }

  getState(): GuestFormState {
    return {
      firstName: cleanText(this.state.firstName),
      lastName: cleanText(this.state.lastName),
      email: this.state.email.trim(),
      phone: this.state.phone.trim(),
      notes: this.state.notes.trim(),
      gdprConsent: this.state.gdprConsent,
    };
  }

  isValid(): boolean {
    if (cleanText(this.state.firstName).length < 1) return false;
    if (cleanText(this.state.firstName).length > MAX_LENGTHS.firstName) return false;
    if (cleanText(this.state.lastName).length < 1) return false;
    if (cleanText(this.state.lastName).length > MAX_LENGTHS.lastName) return false;
    if (!isValidEmail(this.state.email)) return false;
    if (this.state.email.length > MAX_LENGTHS.email) return false;
    if (!isValidPhone(this.state.phone)) return false;
    if (this.state.phone.length > MAX_LENGTHS.phone) return false;
    if (this.state.notes.length > MAX_LENGTHS.notes) return false;
    if (!this.state.gdprConsent) return false;
    return true;
  }

  private errorFor(key: keyof GuestFormState): string | null {
    if (!this.touched[key]) return null;
    switch (key) {
      case 'firstName': {
        const v = cleanText(this.state.firstName);
        if (v.length < 1) return 'Please enter your first name.';
        if (v.length > MAX_LENGTHS.firstName)
          return `First name must be ${MAX_LENGTHS.firstName} characters or fewer.`;
        return null;
      }
      case 'lastName': {
        const v = cleanText(this.state.lastName);
        if (v.length < 1) return 'Please enter your last name.';
        if (v.length > MAX_LENGTHS.lastName)
          return `Last name must be ${MAX_LENGTHS.lastName} characters or fewer.`;
        return null;
      }
      case 'email':
        if (!isValidEmail(this.state.email)) return 'Please enter a valid email address.';
        if (this.state.email.length > MAX_LENGTHS.email)
          return `Email must be ${MAX_LENGTHS.email} characters or fewer.`;
        return null;
      case 'phone':
        if (!isValidPhone(this.state.phone)) return 'Please enter a valid phone number.';
        if (this.state.phone.length > MAX_LENGTHS.phone)
          return `Phone number must be ${MAX_LENGTHS.phone} characters or fewer.`;
        return null;
      case 'notes':
        return this.state.notes.length > MAX_LENGTHS.notes
          ? `Notes must be ${MAX_LENGTHS.notes} characters or fewer.`
          : null;
      case 'gdprConsent':
        return !this.state.gdprConsent ? 'Please consent to continue.' : null;
      default:
        return null;
    }
  }

  /* ----------------------------------------------------------------- */

  private render(): void {
    const privacy = this.opts.privacyUrl ?? '#';
    this.host.innerHTML = `
      <form class="no4w-form" novalidate data-role="guest-form" autocomplete="on">
        <div class="no4w-form-row">
          ${FIELDS.filter((f) => f.half).map((f) => this.renderField(f)).join('')}
        </div>
        ${FIELDS.filter((f) => !f.half).map((f) => this.renderField(f)).join('')}
        <div class="no4w-field">
          <label for="no4w-notes">Notes / special requests (optional)</label>
          <textarea
            id="no4w-notes"
            data-key="notes"
            maxlength="${MAX_LENGTHS.notes}"
            autocomplete="off"
          >${escapeHTML(this.state.notes)}</textarea>
          <p class="no4w-error-text" data-error-for="notes" style="display:none;"></p>
        </div>

        <label class="no4w-checkbox">
          <input
            type="checkbox"
            data-key="gdprConsent"
            ${this.state.gdprConsent ? 'checked' : ''}
          />
          <span>
            I consent to No. 4 High Terrace storing my personal details
            to process this booking, in accordance with the
            <a href="${escapeHTML(privacy)}" target="_blank" rel="noopener">Privacy Notice</a>.
          </span>
        </label>
        <p class="no4w-error-text" data-error-for="gdprConsent" style="display:none;"></p>
      </form>
    `;

    this.bind();
    this.refreshErrors();
  }

  private renderField(f: FieldDef): string {
    const id = `no4w-${f.key}`;
    return `
      <div class="no4w-field">
        <label for="${id}">${escapeHTML(f.label)}${f.required ? ' *' : ''}</label>
        <input
          id="${id}"
          type="${f.type}"
          data-key="${f.key}"
          autocomplete="${f.autocomplete}"
          maxlength="${f.maxLength}"
          value="${escapeHTML(String(this.state[f.key] ?? ''))}"
          ${f.required ? 'required' : ''}
        />
        <p class="no4w-error-text" data-error-for="${f.key}" style="display:none;"></p>
      </div>
    `;
  }

  private bind(): void {
    this.host
      .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
        'input[data-key], textarea[data-key]',
      )
      .forEach((el) => {
        const key = el.dataset.key as keyof GuestFormState;
        if (el instanceof HTMLInputElement && el.type === 'checkbox') {
          el.addEventListener('change', () => {
            this.state.gdprConsent = el.checked;
            this.touched.gdprConsent = true;
            this.opts.onStateChange(this.getState());
            this.refreshErrors();
            this.opts.onValidityChange(this.isValid());
          });
        } else {
          el.addEventListener('input', () => {
            (this.state as unknown as Record<string, unknown>)[key] = el.value;
            this.opts.onStateChange(this.getState());
            this.opts.onValidityChange(this.isValid());
          });
          el.addEventListener('blur', () => {
            this.touched[key] = true;
            this.refreshErrors();
          });
        }
      });
  }

  private refreshErrors(): void {
    (Object.keys(this.touched) as Array<keyof GuestFormState>).forEach((key) => {
      const msg = this.errorFor(key);
      const inputEl = this.host.querySelector<HTMLElement>(
        `[data-key="${key}"]`,
      );
      const errEl = this.host.querySelector<HTMLElement>(
        `[data-error-for="${key}"]`,
      );
      if (inputEl) {
        if (msg) inputEl.classList.add('no4w-field-error');
        else inputEl.classList.remove('no4w-field-error');
      }
      if (errEl) {
        if (msg) {
          errEl.textContent = msg;
          errEl.style.display = '';
        } else {
          errEl.textContent = '';
          errEl.style.display = 'none';
        }
      }
    });
  }
}
